import React, { useState, useEffect } from 'react';
import DragDropLayout, { triggerHaptic } from './Layouts/DragDropLayout';

const AnimalHabitatGame = ({ gameData, onComplete }) => {
    const [matches, setMatches] = useState({});
    const [score, setScore] = useState(0);
    const [feedback, setFeedback] = useState(null); 
    const [shuffledAnimals, setShuffledAnimals] = useState([]);
    const [shuffledHabitats, setShuffledHabitats] = useState([]);
    const [selectedAnimal, setSelectedAnimal] = useState(null);

    useEffect(() => {
        if (gameData?.pairs) {
            setShuffledAnimals([...gameData.pairs].sort(() => Math.random() - 0.5));
            setShuffledHabitats([...gameData.pairs].sort(() => Math.random() - 0.5));
        }
    }, [gameData]);

    const handleAnimalClick = (animal) => {
        if (feedback || matches[animal.id]) return;
        triggerHaptic(30);
        setSelectedAnimal(animal.id === selectedAnimal?.id ? null : animal);
    };

    const handleHabitatClick = (habitatId) => {
        if (!selectedAnimal || feedback || matches[habitatId]) return;

        const isCorrect = String(selectedAnimal.id) === String(habitatId);
        const pair = gameData.pairs.find(p => String(p.id) === String(selectedAnimal.id));

        if (isCorrect) {
            const newScore = score + (gameData.points_per_pair || 20);
            setScore(newScore);
            const newMatches = { ...matches, [habitatId]: pair };
            setMatches(newMatches);
            setFeedback({ status: 'correct', pair: pair });
            setSelectedAnimal(null);
            triggerHaptic([100, 50, 100]);

            setTimeout(() => {
                setFeedback(null);
                if (Object.keys(newMatches).length === gameData.pairs.length) {
                    triggerHaptic([200, 100, 200, 100, 300]);
                    onComplete?.(newScore);
                }
            }, 4500);
        } else {
            setFeedback({ status: 'wrong' });
            triggerHaptic([50, 50, 50]);
            setTimeout(() => setFeedback(null), 1500);
        }
    };

    if (!gameData || !gameData.pairs || shuffledAnimals.length === 0) {
        return (
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-12 text-center border-emerald-100 animate-pulse bg-white">
                <div className="text-6xl md:text-[100px] mb-8">🌿</div>
                <h3 className="text-base md:text-3xl font-black text-emerald-900 tracking-tighter uppercase">Ruošiami namai...</h3>
            </div>
        );
    }

    const progress = (Object.keys(matches).length / gameData.pairs.length) * 100;

    const hud = (
        <div className="premium-glass rounded-xl md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden bg-emerald-50">
            <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/5 rounded-full blur-[80px] pointer-events-none"></div>
            <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
                <div className="flex items-center gap-2 md:gap-4">
                    <div className="w-12 h-12 md:w-16 md:h-16 bg-emerald-100 rounded-xl md:rounded-2xl flex items-center justify-center text-xl md:text-3xl shadow-inner animate-float">
                        🐾
                    </div>
                    <div>
                        <h3 className="text-base md:text-3xl font-black text-slate-800 tracking-tight leading-tight uppercase">
                            Gyvūnų Namų Atlasas
                        </h3>
                    </div>
                </div>

                <div className="flex items-center gap-2 md:gap-4">
                    <div className="bg-white/50 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl border border-white/50 shadow-inner text-center">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Žinios</p>
                        <p className="text-sm md:text-2xl font-black text-emerald-600 leading-none">⭐ {score}</p>
                    </div>
                    <div className="bg-white/50 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl border border-white/50 shadow-inner text-center min-w-[80px] md:min-w-[100px]">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Saugūs</p>
                        <p className="text-sm md:text-2xl font-black text-slate-800 leading-none">{Object.keys(matches).length}/{gameData.pairs.length}</p>
                    </div>
                </div>
            </div>

            <div className="mt-4 md:mt-6 w-full bg-slate-200/50 rounded-full h-2 md:h-3 p-0.5 md:p-1 shadow-inner overflow-hidden border border-slate-300">
                <div 
                    className="h-full bg-gradient-to-r from-emerald-400 via-green-500 to-yellow-600 rounded-full transition-all duration-1000 shadow-[0_0_15px_rgba(16,185,129,0.3)]"
                    style={{ width: `${progress}%` }}
                />
            </div>
        </div>
    );

    const choices = (
        <div className="space-y-4 md:space-y-8 py-4 md:py-8">
            <div className="flex items-center gap-3 px-4">
                <div className="w-3 h-3 bg-orange-500 rounded-full animate-pulse"></div>
                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.4em]">Keliautojai</h4>
            </div>
            <div className="premium-glass rounded-[3.5rem] p-3 md:p-8 shadow-2xl border-white/60 bg-white/40 backdrop-blur-xl relative overflow-hidden flex flex-col gap-4">
                <div className="grid grid-cols-2 gap-4">
                    {shuffledAnimals.map(animal => {
                        const isMatched = !!matches[animal.id];
                        const isSelected = selectedAnimal?.id === animal.id;

                        return (
                            <button
                                key={animal.id}
                                onClick={() => handleAnimalClick(animal)}
                                disabled={isMatched || !!feedback}
                                className={`
                                    premium-button-3d relative p-4 md:p-8 rounded-2xl md:rounded-[2.5rem] flex flex-col items-center gap-2 md:gap-4 transition-all duration-500 border-b-8 md:border-b-[12px]
                                    ${isMatched 
                                        ? 'opacity-20 grayscale scale-90 translate-y-2 border-b-0' 
                                        : isSelected
                                            ? 'bg-orange-500 text-white scale-105 border-orange-700'
                                            : 'bg-white text-slate-800 border-slate-100 active:translate-y-1 active:border-b-0'}
                                    disabled:cursor-default
                                `}
                            >
                                <div className={`text-4xl md:text-7xl drop-shadow-2xl transition-transform ${isSelected ? 'animate-bounce' : ''}`}>
                                    {animal.animal_emoji}
                                </div>
                                <span className="text-[10px] md:text-lg font-black uppercase tracking-tighter leading-none">{animal.animal}</span>
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );

    const targets = (
        <div className="space-y-4 md:space-y-6 flex flex-col items-center w-full max-w-4xl mx-auto">
            <div className="flex items-center gap-2 px-4 self-start">
                <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                <h4 className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-[0.4em]">Buveinių Tinklas</h4>
            </div>
            <div className="premium-glass rounded-2xl md:rounded-[4rem] p-3 md:p-8 shadow-2xl border-white/60 bg-slate-900 relative overflow-hidden w-full min-h-[500px]">
                <div className="absolute top-0 right-0 p-4 md:p-12 opacity-[0.05] text-6xl md:text-[180px] font-black select-none pointer-events-none text-white">🏞️</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 relative z-10">
                    {shuffledHabitats.map(habitat => {
                        const matchedAnimal = matches[habitat.id];
                        const canAccept = selectedAnimal && !matchedAnimal;

                        return (
                            <button
                                key={habitat.id}
                                onClick={() => handleHabitatClick(habitat.id)}
                                disabled={matchedAnimal || !!feedback}
                                className={`
                                    premium-glass rounded-[2rem] md:rounded-[3.5rem] p-6 md:p-12 border-4 transition-all duration-500 flex flex-col items-center justify-center gap-4 relative overflow-hidden group
                                    ${matchedAnimal 
                                        ? 'bg-emerald-500 border-emerald-400 shadow-[0_0_50px_rgba(16,185,129,0.4)]' 
                                        : 'bg-slate-800/80 border-slate-700 hover:border-emerald-500/50'}
                                    ${canAccept ? 'ring-4 ring-emerald-500/50 animate-pulse scale-[1.02]' : ''}
                                `}
                            >
                                {matchedAnimal ? (
                                    <div className="animate-pop-in flex flex-col items-center gap-4">
                                        <div className="w-24 h-24 md:w-32 md:h-32 bg-white rounded-full flex items-center justify-center text-5xl md:text-7xl shadow-2xl border-4 md:border-8 border-emerald-200 animate-bounce">
                                            {matchedAnimal.animal_emoji}
                                        </div>
                                        <div className="text-white text-center">
                                            <p className="text-[10px] font-black uppercase tracking-[0.3em] opacity-60">Saugus Namuose</p>
                                            <p className="text-base md:text-3xl font-black tracking-tighter uppercase">{matchedAnimal.animal}</p>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="text-6xl md:text-9xl drop-shadow-2xl transition-transform group-hover:scale-110">{habitat.habitat_emoji}</div>
                                        <div className="text-center">
                                            <p className="text-[10px] font-black text-emerald-400 uppercase tracking-[0.3em]">Buveinė</p>
                                            <p className="text-base md:text-3xl font-black text-white tracking-tighter uppercase leading-none">{habitat.habitat}</p>
                                        </div>
                                    </>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );

    return (
        <>
            <DragDropLayout 
                hud={hud}
                choices={choices}
                targets={targets}
            />

            {feedback && feedback.status === 'correct' && (
                <div className="fixed inset-0 flex items-center justify-center z-[2000] p-4">
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-xl animate-fade-in" />
                    <div className="max-w-xl w-full p-8 md:p-16 rounded-[3rem] md:rounded-[4rem] bg-white shadow-2xl relative animate-pop-in border-[10px] md:border-[16px] border-emerald-500">
                        <div className="absolute -top-12 left-1/2 -translate-x-1/2 w-24 h-24 bg-white rounded-full flex items-center justify-center text-4xl shadow-2xl border-4 border-inherit animate-bounce">
                            🌿
                        </div>
                        
                        <div className="text-center space-y-4 pt-4">
                            <h2 className="text-2xl md:text-5xl font-black uppercase tracking-tighter text-emerald-600 leading-none">
                                NUOSTABUS DARBAS!
                            </h2>

                            <div className="bg-emerald-50 p-6 md:p-10 rounded-2xl border-2 border-emerald-100">
                                <p className="text-sm md:text-2xl font-bold text-slate-700 text-center leading-relaxed italic font-serif">
                                    "{feedback.pair.fact}"
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default AnimalHabitatGame;
